import {
  type StaffCatalogItemType,
  type StaffCatalogPlaceReviewQuery,
  staffCatalogItemTypes,
  staffCatalogLocationReviewFilters,
  staffCatalogPlaceReviewRefreshStates,
  staffCatalogPlaceReviewStates,
} from "@lafayette/schemas";

// The filters of the review page live in the address (`?state=NEEDS_REVIEW&refresh=ERROR`), so a link
// shares a view. An address value that is not valid is ignored and named, never sent to the API.

export const REVIEW_PAGE_SIZE = 25;

export type ReviewFilterValues = Readonly<{
  state: string;
  refresh: string;
  location: string;
  type: string;
  countryCode: string;
}>;

export const emptyReviewFilters: ReviewFilterValues = {
  state: "",
  refresh: "",
  location: "",
  type: "",
  countryCode: "",
};

const oneOf = <T extends string>(values: readonly T[], value: string | null): T | undefined =>
  value !== null && (values as readonly string[]).includes(value) ? (value as T) : undefined;

export function reviewQueryFromParams(params: URLSearchParams): Readonly<{
  query: StaffCatalogPlaceReviewQuery;
  ignored: readonly string[];
}> {
  const ignored: string[] = [];
  const query: StaffCatalogPlaceReviewQuery = {};

  const pick = <T extends string>(key: string, allowed: readonly T[]): T | undefined => {
    const raw = params.get(key);
    if (raw === null || raw === "") return undefined;
    const value = oneOf(allowed, raw);
    if (value === undefined) ignored.push(key);
    return value;
  };

  const state = pick("state", staffCatalogPlaceReviewStates);
  if (state !== undefined) query.state = state;
  const refresh = pick("refresh", staffCatalogPlaceReviewRefreshStates);
  if (refresh !== undefined) query.refresh = refresh;
  const location = pick("location", staffCatalogLocationReviewFilters);
  if (location !== undefined) query.location = location;
  const type = pick<StaffCatalogItemType>("type", staffCatalogItemTypes);
  if (type !== undefined) query.type = type;
  const country = params.get("countryCode");
  if (country !== null && country !== "") {
    if (/^[A-Z]{2}$/.test(country)) query.countryCode = country;
    else ignored.push("countryCode");
  }
  return { query, ignored };
}

export function reviewValuesFromQuery(query: StaffCatalogPlaceReviewQuery): ReviewFilterValues {
  return {
    state: query.state ?? "",
    refresh: query.refresh ?? "",
    location: query.location ?? "",
    type: query.type ?? "",
    countryCode: query.countryCode ?? "",
  };
}

export function reviewParamsFromValues(values: ReviewFilterValues): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    const trimmed = key === "countryCode" ? value.trim().toUpperCase() : value;
    if (trimmed !== "") params.set(key, trimmed);
  }
  return params;
}

export function activeReviewFilterCount(query: StaffCatalogPlaceReviewQuery): number {
  return [query.state, query.refresh, query.location, query.type, query.countryCode].filter(
    (value) => value !== undefined,
  ).length;
}
